import React, { ReactNode, useState, Dispatch, SetStateAction } from 'react';
import { b2cAddressesApi } from '../api/b2c/addresses';
import {
  IAddressesCommonData,
  IMappedAddress,
  TAddressFormMode,
  TCommonPartialAddress,
} from '../models/common/addresses';
import { addressesApi, ISortConfig } from '../api/common/addresses';
import { IB2BAddress, TB2BRequestAddressData } from '../models/b2b/addresses';
import {
  IB2CAddressWithMetaData,
  TB2CRequestAddressData,
} from '../models/b2c/addresses';
import { isLoggedIn } from '../utils/user';

type IAddressesProviderProps = {
  children: ReactNode;
  isB2B: boolean;
};

export type IAddressesProviderValues = {
  isB2B: boolean;
  addresses: IAddressesCommonData | null;
  addressFormMode: TAddressFormMode;
  editableAddress: IMappedAddress | null;
  defaultAddress: TCommonPartialAddress | null;
  defaultCheckoutAddress: TCommonPartialAddress | null;
  setAddressFormMode: Dispatch<SetStateAction<TAddressFormMode>>;
  setEditableAddress: Dispatch<SetStateAction<IMappedAddress | null>>;
  getAddresses: (options: ISortConfig) => Promise<IAddressesCommonData>;
  changeDefault: (
    newDefId: number | null,
    newDefAdr?: TCommonPartialAddress,
  ) => Promise<IB2BAddress | IB2CAddressWithMetaData>;
};

const AddressesContext = React.createContext<IAddressesProviderValues | null>(
  null,
);

const AddressesProvider = ({ children, isB2B }: IAddressesProviderProps) => {
  const [addresses, setAddresses] =
    useState<IAddressesProviderValues['addresses']>(null);
  const [addressFormMode, setAddressFormMode] =
    useState<TAddressFormMode>(null);
  const [editableAddress, setEditableAddress] =
    useState<IAddressesProviderValues['editableAddress']>(null);
  const [defaultAddress, setDefaultAddress] = useState<
    IAddressesProviderValues['defaultAddress'] | null
  >(null);
  const [defaultCheckoutAddress, setDefaultCheckoutAddress] = useState<
    IAddressesProviderValues['defaultCheckoutAddress'] | null
  >(null);

  const getAddresses = (options: ISortConfig) => {
    if (!isLoggedIn()) {
      return Promise.reject(new Error('user is not logged in'));
    }
    return addressesApi.getAddresses(isB2B, options).then((data) => {
      setAddresses(data);
      setDefaultAddress(data.response.find((adr) => adr.isDefault) || null);
      setDefaultCheckoutAddress(
        data.response.find((adr) => adr.instantCheckoutDefault) || null,
      );
      return data;
    });
  };

  const changeDefault = async (
    newDefId: number | null,
    newDefAdr?: TCommonPartialAddress,
  ) => {
    const newDefault =
      newDefAdr ||
      addresses?.response.find((adr) => adr.addrIdentifier === newDefId);

    let currentDefAdr = defaultAddress;
    if (!currentDefAdr && !isB2B) {
      await b2cAddressesApi.getDefaultAddress().then(({ data }) => {
        currentDefAdr =
          data.addresses.address.find((adr) => adr.isDefault) || null;
      });
    }

    return addressesApi
      .changeDefaultAddress(
        isB2B,
        {
          ...(newDefault as TB2CRequestAddressData | TB2BRequestAddressData),
          isDefault: true,
        },
        // catch scenarios where customer has only one address that is NOT set as default
        isB2B || !currentDefAdr
          ? null
          : {
              ...(currentDefAdr as TB2CRequestAddressData),
              isDefault: false,
            },
      )
      .then((defaultAdr) => {
        setDefaultAddress(defaultAdr);
        return defaultAdr;
      });
  };

  return (
    <AddressesContext.Provider
      value={{
        addresses,
        isB2B,
        addressFormMode,
        editableAddress,
        defaultAddress,
        defaultCheckoutAddress,
        setAddressFormMode,
        setEditableAddress,
        getAddresses,
        changeDefault,
      }}
    >
      {children}
    </AddressesContext.Provider>
  );
};

export { AddressesProvider, AddressesContext };
